import { supabase } from './supabase';
import { sendSMS } from './messaging';
import { sendEmail, sendEmailWithFallback } from './email';
import { createProposalCreatedMilestone, createProposalSentMilestone, createProposalViewedMilestone, createProposalApprovedMilestone } from './bookingMilestones';
import { getSiteUrl } from './siteConfig';
import { getProposalPublicOriginFromBusinessRow, buildPublicProposalUrl } from './proposalPublicUrl';
import { InvoiceSettingsService } from '../services/invoiceSettingsService';
import { recordOpportunityActivity } from './opportunityActivities';
import { findOpenLinkableOpportunityForCustomer } from './crm/opportunityLinking';

export interface ProposalService {
  id: string;
  proposal_id: string;
  offer_id: string;
  service_id: string;
  vehicle_id: string | null;
  name: string;
  description: string | null;
  price: number;
  originalPrice?: number;
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
  tax_exempt: boolean;
  tax_rate: number | null;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  duration_minutes: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_year: string | null;
  vehicle_color: string | null;
  vehicle_vin: string | null;
  vehicle_license_plate: string | null;
  vehicle_is_primary: boolean | null;
  isSelectable?: boolean;
  pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
  unit_price?: number;
  duration_per_unit?: number;
  quantity?: number;
  measurement_id?: string;
  /** Display-only compare-at / "was" price snapshotted from the chosen price option. */
  compare_at_price?: number | null;
  /** Stable per-line identity (survives the delete/recreate of service rows on edit). Used to attach per-service photos. */
  line_key?: string | null;
}

export interface ProposalOffer {
  id: string;
  proposal_id: string;
  title: string;
  status: string;
  order_index: number;
  deposit_percentage?: number | null;
  /** When true, online proposal deposit may save card to CRM customer wallet. */
  save_card_on_deposit?: boolean | null;
  /** When true, public proposal shows the customer address below this offer title. */
  show_customer_address?: boolean | null;
  created_at: string;
  updated_at: string;
  services?: ProposalService[];
}

export interface Proposal {
  id: string;
  business_id: string;
  customer_id: string;
  title: string;
  status: string;
  valid_until: string | null;
  notes: string | null;
  project_id?: string | null;
  booking_id?: string | null;
  field_quote_id?: string | null;
  pipeline_opportunities_id?: number | null;
  proposal_number?: number | null;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  created_at: string;
  updated_at: string;
  customer?: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  offers?: ProposalOffer[];
}

interface CreateProposalParams {
  business_id: string;
  customer_id: string;
  title: string;
  valid_until?: string;
  expiry_date?: string;
  notes?: string;
  project_id?: string;
  booking_id?: string;
  pipeline_opportunities_id?: number;
  offers: {
    title: string;
    deposit_percentage?: number | null;
    save_card_on_deposit?: boolean | null;
    show_customer_address?: boolean | null;
    services: {
      service_id: string;
      vehicle_id: string | null;
      name: string;
      description?: string;
      price: number;
      originalPrice?: number;
      discountType?: 'percentage' | 'dollar';
      discountValue?: number;
      tax_exempt?: boolean;
      tax_rate?: number;
      discount_amount?: number;
      duration_minutes: number;
      isSelectable?: boolean;
      pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
      unit_price?: number;
      duration_per_unit?: number;
      quantity?: number;
      measurement_id?: string;
      line_key?: string;
    }[];
    products?: {
      stock_item_id: string;
      vehicle_id: string | null;
      name: string;
      price: number;
      quantity: number;
      category_name: string;
      discountType?: 'percentage' | 'dollar';
      discountValue?: number;
    }[];
  }[];
}

interface CreateProposalServiceParams {
  service_id: string;
  vehicle_id: string | null;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
  tax_exempt?: boolean;
  tax_rate?: number;
  discount_amount?: number;
  duration_minutes: number;
  isSelectable?: boolean;
}

export async function getProposals(businessId: string, options?: { limit?: number; offset?: number }) {
  const { limit = 30, offset = 0 } = options || {};

  const { data, error } = await supabase
    .from('proposals')
    .select(`
      *,
      customer:customers!proposals_customer_id_fkey(
        first_name,
        last_name,
        email,
        phone
      ),
      offers:proposal_offers(
        *,
        services:proposal_services_with_details(*),
        products:proposal_products_with_details(*)
      )
    `)
    .eq('business_id', businessId)
    .neq('status', 'held')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data as Proposal[];
}

const PROPOSAL_LIST_SELECT = `
  *,
  customer:customers!proposals_customer_id_fkey(
    first_name,
    last_name,
    email,
    phone
  ),
  offers:proposal_offers(
    *,
    services:proposal_services_with_details(*),
    products:proposal_products_with_details(*)
  )
`;

/** Escape `%` / `_` so user search text is treated literally in ilike patterns. */
function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

/**
 * Server-side proposal search (list page only loads ~30 rows client-side).
 * Matches proposal number (#2073), title, or customer name/email.
 */
export async function searchProposals(
  businessId: string,
  rawQuery: string,
  options?: { limit?: number }
): Promise<Proposal[]> {
  const q = rawQuery.trim();
  if (!businessId || !q) return [];

  const { limit = 30 } = options || {};
  const numberMatch = q.match(/^#?(\d+)$/);

  if (numberMatch) {
    const { data, error } = await supabase
      .from('proposals')
      .select(PROPOSAL_LIST_SELECT)
      .eq('business_id', businessId)
      .eq('proposal_number', Number(numberMatch[1]))
      .neq('status', 'held')
      .limit(limit);

    if (error) throw error;
    return (data || []) as Proposal[];
  }

  const pattern = `%${escapeIlike(q)}%`;

  const { data: customers, error: customerError } = await supabase
    .from('customers')
    .select('id')
    .eq('business_id', businessId)
    .or(`first_name.ilike.${pattern},last_name.ilike.${pattern},email.ilike.${pattern}`)
    .limit(50);

  if (customerError) {
    console.error('Error searching customers for proposals:', customerError);
  }

  const customerIds = (customers || []).map((c) => c.id);
  let query = supabase
    .from('proposals')
    .select(PROPOSAL_LIST_SELECT)
    .eq('business_id', businessId)
    .neq('status', 'held')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (customerIds.length > 0) {
    query = query.or(`title.ilike.${pattern},customer_id.in.(${customerIds.join(',')})`);
  } else {
    query = query.ilike('title', pattern);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as Proposal[];
}

/** Statuses that mean a proposal is still actionable when creating a booking. */
export const CONVERTIBLE_PROPOSAL_STATUSES = ['sent', 'viewed', 'approved'] as const;

export type ConvertibleProposalSummary = Pick<
  Proposal,
  'id' | 'title' | 'status' | 'created_at' | 'valid_until' | 'total_amount' | 'proposal_number'
>;

/** True when valid_until is set and already in the past (null/missing = not expired). */
export function isProposalExpired(validUntil: string | null | undefined, now: Date = new Date()): boolean {
  const end = proposalValidUntilEnd(validUntil);
  if (!end) return false;
  return end < now;
}

const EXPIRY_DAY_WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six'] as const;

function proposalValidUntilEnd(validUntil: string | null | undefined): Date | null {
  if (!validUntil) return null;
  const end = new Date(validUntil);
  if (Number.isNaN(end.getTime())) return null;
  // Date-only values (YYYY-MM-DD) are valid through the end of that local day.
  if (/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) {
    end.setHours(23, 59, 59, 999);
  }
  return end;
}

/**
 * Lead-card countdown. Hidden until a week out, then:
 * "Expires in a week" → "Expires in six days" … "Expires in one day" →
 * "Expires today" through the expiry day → "Expired".
 */
export function proposalExpiryBadgeLabel(
  validUntil: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const end = proposalValidUntilEnd(validUntil);
  if (!end) return null;
  if (end < now) return 'Expired';

  const expiryDay = new Date(end);
  expiryDay.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const daysLeft = Math.round((expiryDay.getTime() - today.getTime()) / 86_400_000);

  if (daysLeft > 7 || daysLeft < 0) return null;
  if (daysLeft === 7) return 'Expires in a week';
  if (daysLeft === 0) return 'Expires today';
  if (daysLeft === 1) return 'Expires in one day';
  return `Expires in ${EXPIRY_DAY_WORDS[daysLeft]} days`;
}

/** Lightweight list of open/approved proposals for a customer (booking create prompt). */
export async function getConvertibleProposalsForCustomer(
  businessId: string,
  customerId: string
): Promise<ConvertibleProposalSummary[]> {
  if (!businessId || !customerId) return [];

  const { data, error } = await supabase
    .from('proposals')
    .select('id, title, status, created_at, valid_until, total_amount, proposal_number')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .in('status', [...CONVERTIBLE_PROPOSAL_STATUSES])
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) {
    console.error('Error fetching convertible proposals for customer:', error);
    throw error;
  }

  // Status stays "viewed"/"sent"/"approved" after expiry — never treat those as convertible.
  return ((data || []) as ConvertibleProposalSummary[]).filter(
    (p) => !isProposalExpired(p.valid_until)
  );
}

export async function getProposalsByCustomer(businessId: string, customerId: string, options?: { limit?: number; offset?: number }) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }
  
  if (!customerId || typeof customerId !== 'string') {
    throw new Error('Valid customer ID is required');
  }

  const { limit = 50, offset = 0 } = options || {};

  console.log('Fetching proposals for customer:', customerId, { businessId, limit, offset });
  
  const { data, error } = await supabase
    .from('proposals')
    .select(`
      *,
      customer:customers!proposals_customer_id_fkey(
        first_name,
        last_name,
        email,
        phone
      ),
      offers:proposal_offers(
        *,
        services:proposal_services_with_details(*),
        products:proposal_products_with_details(*)
      )
    `)
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .neq('status', 'held')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching proposals for customer:', error);
    throw error;
  }

  return data as Proposal[];
}

// Get total count of proposals for pagination
export async function getProposalsCount(businessId: string): Promise<number> {
  const { count, error } = await supabase
    .from('proposals')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', businessId);

  if (error) {
    console.error('Error counting proposals:', error);
    throw error;
  }

  return count || 0;
}

export async function getProposal(id: string) {
  const { data, error } = await supabase
    .from('proposals')
    .select(`
      *,
      customer:customers!proposals_customer_id_fkey(
        first_name,
        last_name,
        email,
        phone
      ),
      offers:proposal_offers(
        *,
        services:proposal_services_with_details(*),
        products:proposal_products_with_details(*)
      )
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Proposal;
}

export async function createProposal(params: CreateProposalParams) {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  // If project_id is provided but pipeline_opportunities_id is not explicitly set,
  // check if the project has a linked opportunity and use it
  let opportunityId = params.pipeline_opportunities_id;
  if (params.project_id && !opportunityId) {
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('pipeline_opportunities_id')
        .eq('id', params.project_id)
        .single();
      
      if (!projectError && projectData && projectData.pipeline_opportunities_id) {
        opportunityId = projectData.pipeline_opportunities_id;
        console.log('Auto-linking proposal to opportunity from project:', opportunityId);
      }
    } catch (error) {
      console.error('Error checking project for linked opportunity:', error);
      // Continue without auto-linking if there's an error
    }
  }

  if (!opportunityId && params.customer_id) {
    try {
      const linkableOpportunity = await findOpenLinkableOpportunityForCustomer(
        params.business_id,
        params.customer_id,
      );

      if (linkableOpportunity) {
        opportunityId = linkableOpportunity.id;
        console.log('Auto-linking proposal to open CRM opportunity:', opportunityId);
      }
    } catch (error) {
      console.error('Error checking customer for open CRM opportunity:', error);
    }
  }
  
  // Fetch business tax rates for accurate product tax calculation
  const { data: bizTaxRates } = await supabase
    .from('tax_rates')
    .select('name, rate')
    .eq('business_id', params.business_id);
  const combinedTaxRate = (bizTaxRates || []).reduce((sum: number, r: any) => sum + r.rate, 0) / 100;

  // Create proposal
  const { data: proposalData, error: proposalError } = await supabase
    .from('proposals')
    .insert([{
      business_id: params.business_id,
      customer_id: params.customer_id,
      title: params.title,
      valid_until: params.expiry_date || params.valid_until,
      notes: params.notes,
      project_id: params.project_id,
      booking_id: params.booking_id || null,
      pipeline_opportunities_id: opportunityId,
      status: 'draft'
    }])
    .select()
    .single();

  if (proposalError) throw proposalError;

  // Create offers and services
  for (const [offerIndex, offer] of params.offers.entries()) {
    // Create offer
    const { data: offerData, error: offerError } = await supabase
      .from('proposal_offers')
      .insert([{
        proposal_id: proposalData.id,
        title: offer.title,
        order_index: offerIndex,
        deposit_percentage: (offer as any).deposit_percentage || null,
        save_card_on_deposit: !!(offer as any).save_card_on_deposit,
        show_customer_address: !!(offer as any).show_customer_address,
      }])
      .select()
      .single();

    if (offerError) throw offerError;

    // Create services for this offer
    const { error: servicesError } = await supabase
      .from('proposal_services')
      .insert(
        offer.services.map((service, serviceIndex) => {
          const originalPrice = service.price; // price field contains original price
          
          // Calculate discount amount based on type and value
          let discountAmount = 0;
          if (service.discountValue && service.discountValue > 0) {
            if (service.discountType === 'percentage') {
              discountAmount = originalPrice * (service.discountValue / 100);
            } else {
              discountAmount = service.discountValue;
            }
          }
          
          // Final price after discount
          const finalPrice = Math.max(0, originalPrice - discountAmount);
          
          // Tax calculation on discounted price (rounded to nearest penny)
          const taxAmount = service.tax_exempt ? 0 : Math.round((finalPrice * (service.tax_rate || 5) / 100) * 100) / 100;
          
          return {
            proposal_id: proposalData.id,
            offer_id: offerData.id,
            service_id: service.service_id,
            vehicle_id: service.vehicle_id === 'no-vehicle' ? null : service.vehicle_id,
            name: service.name,
            description: service.description || '',
            price: originalPrice,
            discount_type: service.discountType,
            discount_value: service.discountValue,
            tax_exempt: service.tax_exempt || false,
            tax_rate: service.tax_rate || 5,
            tax_amount: taxAmount,
            discount_amount: Math.round(discountAmount * 100) / 100,
            total_amount: Math.round((finalPrice + taxAmount) * 100) / 100,
            duration_minutes: service.duration_minutes,
            order_index: serviceIndex,
            is_selectable: service.isSelectable || false,
            is_selected: !(service.isSelectable || false),
            pricing_type: service.pricing_type || 'flat',
            unit_price: service.unit_price || 0,
            duration_per_unit: service.duration_per_unit || 0,
            quantity: service.quantity || 0,
            measurement_id: service.measurement_id || null,
            compare_at_price: (service as any).compare_at_price ?? null,
            line_key: (service as any).line_key || null
          };
        })
      );

    if (servicesError) throw servicesError;

    // Create products for this offer (if any)
    if (offer.products && offer.products.length > 0) {
      const { error: productsError } = await supabase
        .from('proposal_products')
        .insert(
          offer.products.map((product, productIndex) => {
            const originalPrice = product.price;
            
            // Calculate discount amount based on type and value
            let discountAmount = 0;
            if (product.discountValue && product.discountValue > 0) {
              if (product.discountType === 'percentage') {
                discountAmount = originalPrice * (product.discountValue / 100);
              } else {
                discountAmount = product.discountValue;
              }
            }
            
            // Final price after discount
            const finalPrice = Math.max(0, originalPrice - discountAmount);
            
            const taxAmount = Math.round((finalPrice * combinedTaxRate) * 100) / 100;
            
            const totalAmount = Math.round(((finalPrice * product.quantity) + (taxAmount * product.quantity)) * 100) / 100;
            
            return {
              proposal_id: proposalData.id,
              offer_id: offerData.id,
              stock_item_id: product.stock_item_id,
              vehicle_id: product.vehicle_id === 'no-vehicle' ? null : product.vehicle_id,
              name: product.name,
              price: originalPrice,
              quantity: product.quantity,
              category_name: product.category_name,
              discount_type: product.discountType,
              discount_value: product.discountValue,
              tax_exempt: false,
              tax_rate: Math.round((bizTaxRates || []).reduce((s: number, r: any) => s + r.rate, 0) * 100) / 100,
              tax_amount: Math.round((taxAmount * product.quantity) * 100) / 100,
              discount_amount: Math.round((discountAmount * product.quantity) * 100) / 100,
              total_amount: totalAmount,
              order_index: productIndex
            };
          })
        );

      if (productsError) throw productsError;
    }
  }

  // Calculate and update proposal totals
  const { data: allServices, error: servicesQueryError } = await supabase
    .from('proposal_services')
    .select('price, discount_amount, tax_amount, total_amount')
    .eq('proposal_id', proposalData.id);

  if (servicesQueryError) throw servicesQueryError;

  // Also get all products for this proposal
  const { data: allProducts, error: productsQueryError } = await supabase
    .from('proposal_products')
    .select('price, quantity, discount_amount, tax_amount, total_amount')
    .eq('proposal_id', proposalData.id);

  if (productsQueryError) throw productsQueryError;

  // Calculate totals from both services and products
  const serviceSubtotal = (allServices || []).reduce((sum, service) => sum + (service.price || 0), 0);
  const serviceDiscountAmount = (allServices || []).reduce((sum, service) => sum + (service.discount_amount || 0), 0);
  const serviceTaxAmount = (allServices || []).reduce((sum, service) => sum + (service.tax_amount || 0), 0);
  const serviceTotalAmount = (allServices || []).reduce((sum, service) => sum + (service.total_amount || 0), 0);

  const productSubtotal = (allProducts || []).reduce((sum, product) => sum + ((product.price || 0) * (product.quantity || 1)), 0);
  const productDiscountAmount = (allProducts || []).reduce((sum, product) => sum + (product.discount_amount || 0), 0);
  const productTaxAmount = (allProducts || []).reduce((sum, product) => sum + (product.tax_amount || 0), 0);
  const productTotalAmount = (allProducts || []).reduce((sum, product) => sum + (product.total_amount || 0), 0);

  // Combined totals
  const subtotal = serviceSubtotal + productSubtotal;
  const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
  const totalTaxAmount = serviceTaxAmount + productTaxAmount;
  const totalAmount = serviceTotalAmount + productTotalAmount;

  // Update proposal with calculated totals (only if there are services or products)
  if ((allServices && allServices.length > 0) || (allProducts && allProducts.length > 0)) {
    const { error: updateTotalsError } = await supabase
      .from('proposals')
      .update({
        subtotal: subtotal,
        discount_amount: totalDiscountAmount,
        tax_amount: totalTaxAmount,
        total_amount: totalAmount
      })
      .eq('id', proposalData.id);

    if (updateTotalsError) throw updateTotalsError;
  }

  // Update project with proposal_id if project_id was provided (bidirectional link)
  if (params.project_id) {
    try {
      // Prepare update data
      const updateData: { proposal_id: string; pipeline_opportunities_id?: number } = {
        proposal_id: proposalData.id
      };
      
      // If proposal has a linked opportunity, also link the project to that opportunity
      if (opportunityId) {
        updateData.pipeline_opportunities_id = opportunityId;
        console.log('Auto-linking project to opportunity from proposal:', opportunityId);
      }
      
      const { error: updateProjectError } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', params.project_id);

      if (updateProjectError) {
        console.error('Error linking proposal to project:', updateProjectError);
        // Don't throw here as the proposal was created successfully
      } else {
        console.log('✅ Proposal linked to project successfully');
        if (opportunityId) {
          console.log('✅ Project also linked to opportunity successfully');
        }
      }
    } catch (error) {
      console.error('Error linking proposal to project:', error);
      // Don't throw here as the proposal was created successfully
    }
  }

  // Create proposal milestone for tracking
  try {
    console.log('🎯 Creating proposal milestone:', {
      business_id: params.business_id,
      customer_id: params.customer_id,
      proposal_id: proposalData.id,
      title: params.title
    });
    
    await createProposalCreatedMilestone(
      params.business_id,
      params.customer_id,
      proposalData.id,
      params.title
    );
    
    console.log('✅ Proposal milestone created successfully');
  } catch (milestoneError) {
    console.error('❌ Error creating proposal milestone:', milestoneError);
    console.error('Full error details:', JSON.stringify(milestoneError, null, 2));
    // Don't throw here as the proposal was created successfully
  }

  return getProposal(proposalData.id);
}

export async function updateProposal(
  id: string,
  updates: Partial<Proposal> & {
    offers?: {
      id?: string;
      title: string;
      deposit_percentage?: number | null;
      save_card_on_deposit?: boolean | null;
      show_customer_address?: boolean | null;
      services: {
        id?: string;
        service_id: string;
        vehicle_id: string | null;
        name: string;
        description?: string;
        price: number;
        originalPrice?: number;
        discountType?: 'percentage' | 'dollar';
        discountValue?: number;
        tax_exempt?: boolean;
        tax_rate?: number;
        discount_amount?: number;
        duration_minutes: number;
        isSelectable?: boolean;
        line_key?: string;
      }[];
      products?: {
        id?: string;
        stock_item_id: string;
        vehicle_id: string | null;
        name: string;
        acquisition_cost?: number;
        price: number;
        quantity: number;
        category_name: string;
        discountType?: 'percentage' | 'dollar';
        discountValue?: number;
      }[];
    }[];
  }
) {
  const { offers, ...proposalUpdates } = updates;

  // Update proposal
  const { error: proposalError } = await supabase
    .from('proposals')
    .update(proposalUpdates)
    .eq('id', id);

  if (proposalError) throw proposalError;

  // Fetch business tax rates for product tax calculation
  const { data: existingProposal } = await supabase
    .from('proposals')
    .select('business_id')
    .eq('id', id)
    .single();
  let updateCombinedTaxRate = 0.05;
  if (existingProposal?.business_id) {
    const { data: updBizTaxRates } = await supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', existingProposal.business_id);
    if (updBizTaxRates && updBizTaxRates.length > 0) {
      updateCombinedTaxRate = updBizTaxRates.reduce((sum: number, r: any) => sum + r.rate, 0) / 100;
    }
  }

  // Update offers if provided
  if (offers) {
    // Delete existing offers, services, and products
    const { error: deleteProductsError } = await supabase
      .from('proposal_products')
      .delete()
      .in('proposal_id', [id]);

    if (deleteProductsError) throw deleteProductsError;

    const { error: deleteServicesError } = await supabase
      .from('proposal_services')
      .delete()
      .eq('proposal_id', id);

    if (deleteServicesError) throw deleteServicesError;

    const { error: deleteOffersError } = await supabase
      .from('proposal_offers')
      .delete()
      .eq('proposal_id', id);

    if (deleteOffersError) throw deleteOffersError;

    // Create new offers, services, and products
    for (const [offerIndex, offer] of offers.entries()) {
      const { data: offerData, error: offerError } = await supabase
        .from('proposal_offers')
        .insert([{
          proposal_id: id,
          title: offer.title,
          order_index: offerIndex,
          deposit_percentage: offer.deposit_percentage || null,
          save_card_on_deposit: !!offer.save_card_on_deposit,
          show_customer_address: !!offer.show_customer_address,
        }])
        .select()
        .single();

      if (offerError) throw offerError;

      // Create services for this offer
      if (offer.services && offer.services.length > 0) {
        const originalPrice = offer.services[0].price; // price field contains original price
        
        // Calculate discount amount based on type and value
        let discountAmount = 0;
        if (offer.services[0].discountValue && offer.services[0].discountValue > 0) {
          if (offer.services[0].discountType === 'percentage') {
            discountAmount = originalPrice * (offer.services[0].discountValue / 100);
          } else {
            discountAmount = offer.services[0].discountValue;
          }
        }
        
        // Final price after discount
        const finalPrice = Math.max(0, originalPrice - discountAmount);
        
        // Tax calculation on discounted price
        const taxAmount = offer.services[0].tax_exempt ? 0 : Math.round((finalPrice * (offer.services[0].tax_rate || 5) / 100) * 100) / 100;

        const { error: servicesError } = await supabase
          .from('proposal_services')
          .insert(
            offer.services.map((service, serviceIndex) => {
              const serviceOriginalPrice = service.originalPrice || service.price;
              
              // Calculate discount amount based on type and value
              let serviceDiscountAmount = 0;
              if (service.discountValue && service.discountValue > 0) {
                if (service.discountType === 'percentage') {
                  serviceDiscountAmount = serviceOriginalPrice * (service.discountValue / 100);
                } else {
                  serviceDiscountAmount = service.discountValue;
                }
              }
              
              // Final price after discount
              const serviceFinalPrice = Math.max(0, serviceOriginalPrice - serviceDiscountAmount);
              
              // Tax calculation on discounted price
              const serviceTaxAmount = service.tax_exempt ? 0 : Math.round((serviceFinalPrice * (service.tax_rate || 5) / 100) * 100) / 100;

              return {
                proposal_id: id,
                offer_id: offerData.id,
                service_id: service.service_id,
                vehicle_id: service.vehicle_id === 'no-vehicle' ? null : service.vehicle_id,
                name: service.name,
                description: service.description || '',
                price: serviceOriginalPrice,
                discount_type: service.discountType,
                discount_value: service.discountValue,
                tax_exempt: service.tax_exempt || false,
                tax_rate: service.tax_rate || 5,
                tax_amount: serviceTaxAmount,
                discount_amount: Math.round(serviceDiscountAmount * 100) / 100,
                total_amount: Math.round((serviceFinalPrice + serviceTaxAmount) * 100) / 100,
                duration_minutes: service.duration_minutes,
                order_index: serviceIndex,
                is_selectable: service.isSelectable || false,
                is_selected: !(service.isSelectable || false),
                pricing_type: service.pricing_type || 'flat',
                unit_price: service.unit_price || 0,
                duration_per_unit: service.duration_per_unit || 0,
                quantity: service.quantity || 0,
                measurement_id: service.measurement_id || null,
                compare_at_price: (service as any).compare_at_price ?? null,
                line_key: (service as any).line_key || null
              };
            })
          );

        if (servicesError) throw servicesError;
      }

      // Create products for this offer (if any)
      if (offer.products && offer.products.length > 0) {
        const { error: productsError } = await supabase
          .from('proposal_products')
          .insert(
            offer.products.map((product, productIndex) => {
              const originalPrice = product.price;
              
              // Calculate discount amount based on type and value
              let discountAmount = 0;
              if (product.discountValue && product.discountValue > 0) {
                if (product.discountType === 'percentage') {
                  discountAmount = originalPrice * (product.discountValue / 100);
                } else {
                  discountAmount = product.discountValue;
                }
              }
              
              // Final price after discount
              const finalPrice = Math.max(0, originalPrice - discountAmount);
              
              const taxAmount = Math.round((finalPrice * updateCombinedTaxRate) * 100) / 100;
              
              const totalAmount = Math.round(((finalPrice * product.quantity) + (taxAmount * product.quantity)) * 100) / 100;
              
              return {
                proposal_id: id,
                offer_id: offerData.id,
                stock_item_id: product.stock_item_id,
                vehicle_id: product.vehicle_id === 'no-vehicle' ? null : product.vehicle_id,
                name: product.name,
                price: originalPrice,
                quantity: product.quantity,
                category_name: product.category_name,
                discount_type: product.discountType,
                discount_value: product.discountValue,
                tax_exempt: false,
                tax_rate: Math.round(updateCombinedTaxRate * 10000) / 100,
                tax_amount: Math.round((taxAmount * product.quantity) * 100) / 100,
                discount_amount: Math.round((discountAmount * product.quantity) * 100) / 100,
                total_amount: totalAmount,
                order_index: productIndex
              };
            })
          );

        if (productsError) throw productsError;
      }
    }

    // Recalculate and update proposal totals
    const { data: allServices, error: servicesQueryError } = await supabase
      .from('proposal_services')
      .select('price, discount_amount, tax_amount, total_amount')
      .eq('proposal_id', id);

    if (servicesQueryError) throw servicesQueryError;

    const { data: allProducts, error: productsQueryError } = await supabase
      .from('proposal_products')
      .select('price, quantity, discount_amount, tax_amount, total_amount')
      .eq('proposal_id', id);

    if (productsQueryError) throw productsQueryError;

    // Calculate totals from both services and products
    const serviceSubtotal = (allServices || []).reduce((sum, service) => sum + (service.price || 0), 0);
    const serviceDiscountAmount = (allServices || []).reduce((sum, service) => sum + (service.discount_amount || 0), 0);
    const serviceTaxAmount = (allServices || []).reduce((sum, service) => sum + (service.tax_amount || 0), 0);
    const serviceTotalAmount = (allServices || []).reduce((sum, service) => sum + (service.total_amount || 0), 0);

    const productSubtotal = (allProducts || []).reduce((sum, product) => sum + ((product.price || 0) * (product.quantity || 1)), 0);
    const productDiscountAmount = (allProducts || []).reduce((sum, product) => sum + (product.discount_amount || 0), 0);
    const productTaxAmount = (allProducts || []).reduce((sum, product) => sum + (product.tax_amount || 0), 0);
    const productTotalAmount = (allProducts || []).reduce((sum, product) => sum + (product.total_amount || 0), 0);

    // Combined totals
    const subtotal = serviceSubtotal + productSubtotal;
    const totalDiscountAmount = serviceDiscountAmount + productDiscountAmount;
    const totalTaxAmount = serviceTaxAmount + productTaxAmount;
    const totalAmount = serviceTotalAmount + productTotalAmount;

    // Update proposal with calculated totals
    if ((allServices && allServices.length > 0) || (allProducts && allProducts.length > 0)) {
      const { error: updateTotalsError } = await supabase
        .from('proposals')
        .update({
          subtotal: subtotal,
          discount_amount: totalDiscountAmount,
          tax_amount: totalTaxAmount,
          total_amount: totalAmount
        })
        .eq('id', id);

      if (updateTotalsError) throw updateTotalsError;
    }
  }

  return getProposal(id);
}

export async function deleteProposal(id: string) {
  // First, get the proposal to check if it's linked to a project or opportunity
  const { data: proposal, error: fetchError } = await supabase
    .from('proposals')
    .select('project_id, pipeline_opportunities_id')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // If the proposal is linked to a project, remove the proposal_id from the project
  if (proposal?.project_id) {
    const { error: updateError } = await supabase
      .from('projects')
      .update({ proposal_id: null })
      .eq('id', proposal.project_id);

    if (updateError) {
      console.error('Error removing proposal_id from project:', updateError);
      // Don't throw here - we still want to delete the proposal even if updating the project fails
    }
  }

  // Note: When proposal is deleted, pipeline_opportunities_id is automatically removed
  // since it's part of the proposal row. No need to update opportunities table.

  // Delete the proposal
  const { error } = await supabase
    .from('proposals')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Gets an existing token for a proposal or creates a new one if none exists
 * UPDATED: Works with new constraint system (unique per proposal)
 */
export async function getOrCreateProposalToken(proposalId: string, businessId: string): Promise<string> {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    throw new Error('Supabase client is not initialized');
  }

  console.log('Getting or creating proposal token for', proposalId);

  // First, get the proposal's expiry date
  const { data: proposalData, error: proposalError } = await supabase!
    .from('proposals')
    .select('valid_until')
    .eq('id', proposalId)
    .single();

  if (proposalError) {
    console.error('Error fetching proposal data:', proposalError);
    throw proposalError;
  }

  if (!proposalData) {
    console.error('No proposal found for id:', proposalId);
    throw new Error('Proposal not found');
  }

  // Check if we already have a token for this proposal (unique constraint)
  const { data: existingTokens, error: queryError } = await supabase!
    .from('public_proposal_tokens')
    .select('token, expires_at')
    .eq('proposal_id', proposalId)
    .limit(1);

  if (queryError) {
    console.error('Error querying for existing tokens:', queryError);
    throw queryError;
  }

  // If we have a token, check if it's still valid
  if (existingTokens && existingTokens.length > 0) {
    const token = existingTokens[0];
    const tokenExpiresAt = new Date(token.expires_at);
    const proposalExpiresAt = proposalData.valid_until ? new Date(proposalData.valid_until) : null;
    const now = new Date();

    // Check if token is still valid (not expired) AND proposal hasn't expired
    const tokenValid = tokenExpiresAt > now;
    const proposalValid = !proposalExpiresAt || proposalExpiresAt > now;

    if (tokenValid && proposalValid) {
      console.log('Using existing token:', token.token);
      return token.token;
    }
    
    // Token exists but is expired - update it with new expiry
    console.log('Updating expired token with new expiry...');
    
    // Calculate new expiry interval
    let expiresIn = '7 days'; // default fallback
    if (proposalData.valid_until) {
      const proposalExpiry = new Date(proposalData.valid_until);
      const diffMs = proposalExpiry.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays > 0) {
        expiresIn = `${diffDays} days`;
      } else {
        console.log('Proposal already expired, extending token for 1 day for viewing');
        expiresIn = '1 day';
      }
    }

    // Update the existing token's expiry
    const newExpiresAt = new Date();
    const intervalMatch = expiresIn.match(/(\d+)\s*(day|hour|minute)s?/);
    if (intervalMatch) {
      const amount = parseInt(intervalMatch[1]);
      const unit = intervalMatch[2];
      
      switch (unit) {
        case 'day':
          newExpiresAt.setDate(newExpiresAt.getDate() + amount);
          break;
        case 'hour':
          newExpiresAt.setHours(newExpiresAt.getHours() + amount);
          break;
        case 'minute':
          newExpiresAt.setMinutes(newExpiresAt.getMinutes() + amount);
          break;
      }
    }

    const { error: updateError } = await supabase
      .from('public_proposal_tokens')
      .update({ expires_at: newExpiresAt.toISOString() })
      .eq('proposal_id', proposalId);

    if (updateError) {
      console.error('Error updating token expiry:', updateError);
      throw updateError;
    }

    console.log('Token expiry updated successfully');
    return token.token;
  }

  // No token exists, create a new one
  console.log('Creating new proposal token...');
  
  // Calculate expiry interval based on proposal's valid_until date
  let expiresIn = '7 days'; // default fallback
  if (proposalData.valid_until) {
    const proposalExpiry = new Date(proposalData.valid_until);
    const now = new Date();
    const diffMs = proposalExpiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      expiresIn = `${diffDays} days`;
      console.log(`Setting token expiry to match proposal expiry: ${diffDays} days`);
    } else {
      console.log('Proposal already expired, but creating token with 1 day expiry for viewing');
      expiresIn = '1 day';
    }
  }

  const { data: tokenData, error: tokenError } = await supabase
    .rpc('generate_proposal_token', {
      p_proposal_id: proposalId,
      p_business_id: businessId,
      p_expires_in: expiresIn
    });

  if (tokenError) {
    // Handle duplicate key — token was created between our check and insert (race condition)
    if (tokenError.code === '23505') {
      console.log('Token already exists (race condition), fetching existing...');
      const { data: raceToken } = await supabase!
        .from('public_proposal_tokens')
        .select('token')
        .eq('proposal_id', proposalId)
        .limit(1)
        .single();
      if (raceToken?.token) return raceToken.token;
    }
    console.error('Error generating proposal token:', tokenError);
    throw tokenError;
  }

  if (!tokenData) {
    console.error('No token data returned from generate_proposal_token');
    throw new Error('Failed to generate proposal token');
  }

  console.log('Token generated successfully:', tokenData);
  return tokenData;
}

/** Full customer-facing proposal URL (uses verified custom hostname when configured). */
export async function buildProposalCustomerLink(accessToken: string, businessId: string): Promise<string> {
  if (!supabase) {
    return `${getSiteUrl()}/proposal/${accessToken}`;
  }
  const { data, error } = await supabase
    .from('businesses')
    .select('proposal_custom_hostname, proposal_custom_host_verified_at')
    .eq('id', businessId)
    .maybeSingle();
  if (error) {
    console.warn('buildProposalCustomerLink: could not load business host', error);
  }
  const origin = getProposalPublicOriginFromBusinessRow(data);
  return buildPublicProposalUrl(accessToken, origin);
}

/**
 * Logs a proposal event to the proposal_events table
 */
export async function logProposalEvent(
  proposalId: string,
  businessId: string,
  eventType: string,
  message?: string,
  metadata: Record<string, any> = {}
) {
  try {
    console.log(`Logging proposal event: ${eventType} for proposal ${proposalId}`);
    
    const { data, error } = await supabase.rpc('log_proposal_event', {
      p_proposal_id: proposalId,
      p_business_id: businessId,
      p_event_type: eventType,
      p_message: message || null,
      p_metadata: metadata
    });

    if (error) {
      console.error('Error logging proposal event:', error);
      throw error;
    }

    console.log('Successfully logged proposal event');
    return data;
  } catch (error) {
    console.error('Error in logProposalEvent:', error);
    // Don't throw here to prevent disrupting the main flow
    return null;
  }
}

/**
 * Gets the event history for a proposal
 */
export async function getProposalEvents(proposalId: string) {
  console.log('Fetching events for proposal ID:', proposalId);
  
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return [];
    }
    
    // Just retrieve the events without trying to join with user data
    const { data, error } = await supabase
      .from('proposal_events')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('event_timestamp', { ascending: false });

    if (error) {
      console.error('Error fetching proposal events:', error);
      throw error;
    }

    console.log('Retrieved proposal events, count:', data?.length, 'events:', data);
    return data || [];
  } catch (error) {
    console.error('Exception in getProposalEvents:', error);
    // Return empty array instead of throwing to avoid breaking the UI
    return [];
  }
}

// Update the sendProposalLink function to log events
export async function sendProposalLink(proposalId: string, businessId: string, customerPhone: string) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log('Starting to send proposal link:', {
      proposalId,
      businessId,
      customerPhone
    });

    // Get or create a token for this proposal
    const tokenData = await getOrCreateProposalToken(proposalId, businessId);

    const publicUrl = await buildProposalCustomerLink(tokenData, businessId);
    console.log('\n=== PROPOSAL LINK ===');
    console.log(publicUrl);
    console.log('===================\n');

    // Get business name for the message
    let businessName = "your service provider";
    try {
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('name')
        .eq('id', businessId)
        .single();
      
      if (!businessError && business && business.name) {
        businessName = business.name;
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
      // Continue with default business name
    }

    // Send SMS using the existing sendSMS function
    console.log('Sending SMS with proposal link...');
    
    // Format SMS message to prevent iPhone link detection issues
    // By adding line breaks and making the URL more distinct, iOS is less likely to truncate it
    const smsMessage = `You have received a proposal from ${businessName}.

📋 View your proposal:
${publicUrl}

Tap the full link above to view.`;

    const result = await sendSMS({
      businessId,
      to: customerPhone,
      message: smsMessage
    });

    if (!result.success) {
      console.error('Error sending SMS:', result.error);
      throw new Error(result.error || 'Failed to send SMS');
    }

    console.log('SMS sent successfully');

    // Update proposal status to 'sent'
    console.log('Updating proposal status to sent...');
    const { error: updateError } = await supabase
      .from('proposals')
      .update({ status: 'sent' })
      .eq('id', proposalId);

    if (updateError) {
      console.error('Error updating proposal status:', updateError);
      throw updateError;
    }

    // Log the proposal sent event
    await logProposalEvent(
      proposalId, 
      businessId, 
      'sent_sms', 
      'Proposal sent via SMS',
      {
        to_phone: customerPhone,
        url: publicUrl
      }
    );

    // Create proposal sent milestone
    try {
      // Get proposal details for milestone
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .select('title, customer_id')
        .eq('id', proposalId)
        .single();
      
      if (!proposalError && proposal && proposal.customer_id) {
        await createProposalSentMilestone(
          businessId,
          proposal.customer_id,
          proposalId,
          proposal.title,
          'sms',
          customerPhone
        );
        console.log('✅ Proposal sent milestone created successfully');
      } else {
        console.warn('⚠️ Skipping milestone creation - missing customer_id or proposal data:', {
          proposalError,
          proposal,
          customer_id: proposal?.customer_id
        });
      }
    } catch (milestoneError) {
      console.error('❌ Error creating proposal sent milestone:', milestoneError);
      // Don't throw here as the proposal was sent successfully
    }

    console.log('Proposal status updated successfully');
    return { success: true };
  } catch (error) {
    console.error('Error sending proposal link:', error);
    throw error;
  }
}

/**
 * Generates a professional HTML email template for proposal emails
 */
/**
 * Escapes a URL for safe use in HTML href attributes.
 * Gmail and other clients may strip/break links with unescaped &, ", <, >.
 */
function escapeUrlForHtml(url: string): string {
  if (!url || typeof url !== 'string') return '';
  return url
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function generateProposalEmailHTML(
  customerFirstName: string,
  businessName: string,
  proposalTitle: string,
  proposalUrl: string,
  logoUrl?: string,
  customMessage?: string
): string {
  // Escape URL for HTML - prevents Gmail from stripping/broken href
  const safeUrl = escapeUrlForHtml(proposalUrl);
  // Debug logging in template function
  console.log('🎨 generateProposalEmailHTML called with logoUrl:', logoUrl);
  console.log('🎨 logoUrl type:', typeof logoUrl);
  console.log('🎨 logoUrl truthy check:', !!logoUrl);
  console.log('🎨 logoUrl trim check:', logoUrl?.trim());
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Quote: ${proposalTitle}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #F3F4F6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #F3F4F6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; background-color: #FFFFFF; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <!-- Header with Logo and Business Name -->
          <tr>
            <td style="padding: 40px 40px 20px; background-color: #FFFFFF;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  ${logoUrl && logoUrl.trim() ? `
                  <!-- Logo on the left -->
                  <td style="vertical-align: middle; width: auto; padding-right: 16px;">
                    <img src="${logoUrl.replace(/"/g, '&quot;').replace(/'/g, '&#39;')}" alt="${businessName.replace(/"/g, '&quot;').replace(/'/g, '&#39;')}" style="max-height: 40px; max-width: 150px; height: auto; display: block; border: 0; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic;" />
                  </td>
                  ` : ''}
                  <!-- Business name on the right -->
                  <td style="vertical-align: middle; ${logoUrl && logoUrl.trim() ? '' : 'padding: 0;'}">
                    <p style="margin: 0; font-size: 18px; font-weight: 600; color: #111827; line-height: 1.4;">
                      ${businessName}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Divider -->
          <tr>
            <td style="padding: 0 40px; background-color: #FFFFFF;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td style="border-top: 1px solid #E5E7EB; height: 1px; line-height: 1px; font-size: 1px;">&nbsp;</td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Quote Label -->
          <tr>
            <td style="padding: 0 40px 20px;">
              <h1 style="margin: 0; font-size: 32px; font-weight: bold; color: #111827; letter-spacing: -0.5px;">
                Quote
              </h1>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 0 40px 40px;">
              ${customMessage ? `
              <!-- Custom Message (user's edited text - replaces all defaults) -->
              <div style="margin: 0 0 24px;">
                <p style="margin: 0; font-size: 16px; line-height: 1.6; color: #374151; white-space: pre-wrap;">${customMessage.replace(/\n/g, '<br>')}</p>
              </div>
              ` : `
              <!-- Default Message -->
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                Hi ${customerFirstName || 'there'},
              </p>
              
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                Thank you for asking us to quote on your project.
              </p>

              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                If you have any questions or concerns regarding this quote, please don't hesitate to get in touch with us.
              </p>
              `}

              <!-- CTA Button - escaped URL & display:block for Gmail/email client compatibility -->
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin: 32px 0;">
                <tr>
                  <td align="center" style="padding: 0;">
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center">
                      <tr>
                        <td align="center" style="border-radius: 8px; background-color: #2563EB;">
                          <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display: block; padding: 16px 32px; font-size: 16px; font-weight: 600; color: #FFFFFF !important; text-decoration: none; border-radius: 8px; background-color: #2563EB; min-width: 160px;">View Quote</a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
              <!-- Fallback link - if button doesn't work, user can click/copy this -->
              <p style="margin: 16px 0 0 0; font-size: 14px; line-height: 1.6; color: #6B7280;">
                Or copy this link: <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color: #2563EB; text-decoration: underline; word-break: break-all;">${escapeUrlForHtml(proposalUrl)}</a>
              </p>

              <p style="margin: 32px 0 0 0; font-size: 16px; line-height: 1.6; color: #374151;">
                Sincerely,<br>
                ${businessName}
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 30px 40px; background-color: #F9FAFB; border-top: 1px solid #E5E7EB;">
              <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #6B7280; text-align: center;">
                This quote was sent to you by ${businessName}. If you have any questions, please contact us directly.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `;
  
  return html.trim();
}

/**
 * Generates a PDF of the proposal estimate
 */
export async function generateProposalPDF(
  proposal: Proposal,
  business: any,
  invoiceSettings: any
): Promise<{ blob: Blob; filename: string } | null> {
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.error('❌ [PDF] PDF generation requires browser environment');
      return null;
    }

    console.log('📄 [PDF] Starting PDF generation...', {
      proposalId: proposal.id,
      hasOffers: !!proposal.offers,
      offerCount: proposal.offers?.length || 0,
      hasBusiness: !!business,
      hasInvoiceSettings: !!invoiceSettings
    });

    // Import libraries dynamically
    let html2canvas: any;
    let jsPDF: any;
    
    try {
      const modules = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      html2canvas = modules[0].default;
      jsPDF = modules[1].default;
      console.log('📄 [PDF] Libraries loaded successfully');
    } catch (importError) {
      console.error('❌ [PDF] Failed to import PDF libraries:', importError);
      return null;
    }

    console.log('📄 [PDF] Libraries loaded successfully');

    // Create a temporary container for the estimate view
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '800px';
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.padding = '0';
    tempContainer.style.fontFamily = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif';
    
    // Get the offer to display
    const displayOffer = proposal.offers?.find(o => o.status === 'approved') || proposal.offers?.[0];
    if (!displayOffer) {
      console.error('❌ [PDF] No offer found for PDF generation', {
        proposalId: proposal.id,
        offers: proposal.offers
      });
      return null;
    }

    console.log('📄 [PDF] Using offer:', {
      offerId: displayOffer.id,
      offerStatus: displayOffer.status,
      hasServices: !!displayOffer.services,
      serviceCount: displayOffer.services?.length || 0,
      hasProducts: !!(displayOffer as any).products,
      productCount: (displayOffer as any).products?.length || 0
    });

    // Filter services and products
    const services = displayOffer.services?.filter(service => {
      if (displayOffer.status !== 'approved') return true;
      return (service as any).is_selected === true;
    }) || [];

    const products = (displayOffer as any).products?.filter((product: any) => {
      if (displayOffer.status !== 'approved') return true;
      return product.is_selected === true;
    }) || [];

    console.log('📄 [PDF] Filtered items:', {
      serviceCount: services.length,
      productCount: products.length
    });

    if (services.length === 0 && products.length === 0) {
      console.warn('⚠️ [PDF] No services or products to display in PDF');
      // Still generate PDF with header and totals, but warn
    }

    // Calculate totals
    const serviceSubtotal = services.reduce((sum, s) => sum + s.price, 0);
    const productSubtotal = products.reduce((sum: number, p: any) => sum + (p.price * (p.quantity || 1)), 0);
    const subtotal = serviceSubtotal + productSubtotal;
    const totalDiscount = services.reduce((sum, s) => sum + (s.discount_amount || 0), 0);
    const totalTax = services.reduce((sum, s) => {
      if (s.tax_exempt) return sum;
      return sum + (s.tax_amount || 0);
    }, 0);
    const total = subtotal - totalDiscount + totalTax;

    // Build HTML for the estimate - matching the modal's invoice view structure
    const headerBg = invoiceSettings?.header_background || '#F8FAFC';
    const primaryColor = invoiceSettings?.primary_color || '#3B82F6';
    
    const estimateHTML = `
      <div style="background: white; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <!-- Invoice Header -->
        <div style="padding: 24px; border-bottom: 1px solid #f3f4f6; background-color: ${headerBg};">
          <div style="display: flex; flex-direction: column; gap: 16px;">
            <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
              <div>
                ${invoiceSettings?.include_logo && invoiceSettings?.logo_url ? `
                  <div style="width: 64px; height: 64px; background: #e5e7eb; border-radius: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                    <img src="${invoiceSettings.logo_url}" alt="Company Logo" style="width: 100%; height: 100%; object-fit: contain;" />
                  </div>
                ` : ''}
                <h1 style="font-size: 24px; font-weight: bold; color: ${primaryColor}; margin: 0 0 4px 0;">ESTIMATE</h1>
                <p style="font-size: 14px; color: #6b7280; margin: 0;">#${proposal.proposal_number || proposal.id?.slice(0, 8)}</p>
              </div>
              <div style="text-align: left;">
                <h2 style="font-size: 18px; font-weight: bold; color: #111827; margin: 0 0 8px 0;">${invoiceSettings?.company_name || business?.name || ''}</h2>
                ${invoiceSettings?.company_address ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0; white-space: pre-line;">${invoiceSettings.company_address}</p>` : ''}
                ${invoiceSettings?.company_phone ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0;">${invoiceSettings.company_phone}</p>` : ''}
                ${invoiceSettings?.company_email ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0;">${invoiceSettings.company_email}</p>` : ''}
                ${business?.gst_number ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0;">GST: ${business.gst_number}</p>` : ''}
                ${business?.pst_number ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0;">PST: ${business.pst_number}</p>` : ''}
              </div>
            </div>
          </div>
        </div>

        <!-- Customer & Date Info -->
        <div style="padding: 24px; border-bottom: 1px solid #f3f4f6; background: rgba(249, 250, 251, 0.5);">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
            <div>
              <h3 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 8px 0;">Bill To</h3>
              <p style="font-weight: 600; color: #111827; margin: 0 0 4px 0;">${proposal.customer?.first_name || ''} ${proposal.customer?.last_name || ''}</p>
              ${proposal.customer?.email ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0;">${proposal.customer.email}</p>` : ''}
              ${proposal.customer?.phone ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0;">${proposal.customer.phone}</p>` : ''}
            </div>
            <div style="text-align: right;">
              <h3 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 8px 0;">Estimate Date</h3>
              <p style="color: #111827; margin: 0;">${new Date(proposal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              ${(proposal as any).expires_at ? `
                <h3 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 12px 0 8px 0;">Valid Until</h3>
                <p style="color: #111827; margin: 0;">${new Date((proposal as any).expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              ` : proposal.valid_until ? `
                <h3 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 12px 0 8px 0;">Valid Until</h3>
                <p style="color: #111827; margin: 0;">${new Date(proposal.valid_until).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              ` : ''}
            </div>
          </div>
        </div>

        ${proposal.notes ? `
          <!-- Notes -->
          <div style="padding: 24px; border-bottom: 1px solid #f3f4f6; background: white;">
            <h3 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 8px 0;">Notes</h3>
            <p style="font-size: 14px; color: #374151; white-space: pre-wrap; margin: 0;">${proposal.notes}</p>
          </div>
        ` : ''}

        <!-- Line Items -->
        <div style="padding: 24px;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 1px solid #e5e7eb;">
                <th style="text-align: left; padding: 12px 0; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Description</th>
                <th style="text-align: right; padding: 12px 0; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Amount</th>
              </tr>
            </thead>
            <tbody style="border-top: 1px solid #f3f4f6;">
              ${services.map((service: any) => `
                <tr style="border-bottom: 1px solid #f3f4f6;">
                  <td style="padding: 16px 0; vertical-align: top;">
                    <p style="font-weight: 500; color: #111827; margin: 0;">${service.name}</p>
                    ${service.description ? `<p style="font-size: 14px; color: #4b5563; margin: 4px 0; white-space: pre-wrap;">${service.description}</p>` : ''}
                    ${service.vehicle_make && service.vehicle_model ? `<p style="font-size: 12px; color: #6b7280; margin: 4px 0;">${service.vehicle_make} ${service.vehicle_model}</p>` : ''}
                  </td>
                  <td style="padding: 16px 0; text-align: right; vertical-align: top;">
                    ${service.discount_amount && service.discount_amount > 0 ? `
                      <div>
                        <span style="font-size: 14px; color: #9ca3af; text-decoration: line-through; display: block;">$${service.price.toFixed(2)}</span>
                        <span style="font-weight: 500; color: #111827;">$${(service.price - service.discount_amount).toFixed(2)}</span>
                      </div>
                    ` : `
                      <span style="font-weight: 500; color: #111827;">$${service.price.toFixed(2)}</span>
                    `}
                  </td>
                </tr>
              `).join('')}
              ${products.map((product: any) => `
                <tr style="border-bottom: 1px solid #f3f4f6;">
                  <td style="padding: 16px 0; vertical-align: top;">
                    <p style="font-weight: 500; color: #111827; margin: 0;">${product.name}</p>
                    ${product.quantity > 1 ? `<p style="font-size: 12px; color: #6b7280; margin: 4px 0;">Qty: ${product.quantity}</p>` : ''}
                  </td>
                  <td style="padding: 16px 0; text-align: right; vertical-align: top;">
                    <span style="font-weight: 500; color: #111827;">$${(product.price * (product.quantity || 1)).toFixed(2)}</span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div style="padding: 24px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
          <div style="display: flex; justify-content: flex-end;">
            <div style="width: 100%; max-width: 288px;">
              <div style="display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 8px;">
                <span style="color: #4b5563;">Subtotal</span>
                <span style="color: #111827;">$${subtotal.toFixed(2)}</span>
              </div>
              ${totalDiscount > 0 ? `
                <div style="display: flex; justify-content: space-between; font-size: 14px; color: #059669; margin-bottom: 8px;">
                  <span>Discount</span>
                  <span>-$${totalDiscount.toFixed(2)}</span>
                </div>
              ` : ''}
              ${totalTax > 0 ? `
                <div style="display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 8px;">
                  <span style="color: #4b5563;">Tax</span>
                  <span style="color: #111827;">$${totalTax.toFixed(2)}</span>
                </div>
              ` : ''}
              <div style="display: flex; justify-content: space-between; padding-top: 12px; border-top: 1px solid #e5e7eb; margin-top: 8px;">
                <span style="font-size: 18px; font-weight: bold; color: #111827;">Total</span>
                <span style="font-size: 18px; font-weight: bold; color: ${primaryColor};">$${total.toFixed(2)}</span>
              </div>
              ${displayOffer.deposit_percentage && displayOffer.deposit_percentage > 0 ? `
                <div style="display: flex; justify-content: space-between; font-size: 14px; padding-top: 8px; border-top: 1px solid #f3f4f6; margin-top: 8px;">
                  <span style="color: #4b5563;">Deposit Required (${displayOffer.deposit_percentage}%)</span>
                  <span style="font-weight: 500; color: #111827;">$${(total * displayOffer.deposit_percentage / 100).toFixed(2)}</span>
                </div>
              ` : ''}
            </div>
          </div>
        </div>

        ${(invoiceSettings?.payment_terms || invoiceSettings?.thank_you_message) ? `
          <!-- Footer -->
          <div style="padding: 24px; border-top: 1px solid #e5e7eb;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
              ${invoiceSettings?.payment_terms ? `
                <div>
                  <h4 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 8px 0;">Terms & Conditions</h4>
                  <p style="font-size: 14px; color: #4b5563; margin: 0;">${invoiceSettings.payment_terms}</p>
                </div>
              ` : ''}
              ${invoiceSettings?.thank_you_message ? `
                <div style="text-align: right;">
                  <p style="font-size: 14px; font-weight: 500; color: ${primaryColor}; margin: 0;">${invoiceSettings.thank_you_message}</p>
                </div>
              ` : ''}
            </div>
          </div>
        ` : ''}
      </div>
    `;

    tempContainer.innerHTML = estimateHTML;
    document.body.appendChild(tempContainer);

    console.log('📄 [PDF] HTML generated, waiting for render...');

    // Wait for fonts and images to load
    await new Promise(resolve => setTimeout(resolve, 1000));

    console.log('📄 [PDF] Converting to canvas...', {
      containerHeight: tempContainer.scrollHeight,
      containerWidth: tempContainer.scrollWidth
    });

    // Convert to canvas
    let canvas: HTMLCanvasElement;
    try {
      canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight,
        logging: false
      });
    } catch (canvasError) {
      console.error('❌ [PDF] Failed to convert HTML to canvas:', canvasError);
      document.body.removeChild(tempContainer);
      return null;
    }

    console.log('📄 [PDF] Canvas created:', {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });

    // Clean up
    document.body.removeChild(tempContainer);

    // Create PDF
    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    const pdf = new jsPDF('p', 'mm', 'a4');

    // Calculate dimensions to fit A4
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    // Add first page
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Generate filename
    const proposalNumber = proposal.proposal_number || proposal.id?.slice(0, 8);
    const filename = `estimate-${proposalNumber}.pdf`;

    // Get PDF as blob
    const blob = pdf.output('blob');

    console.log('✅ [PDF] PDF generated successfully:', {
      filename,
      blobSize: blob.size,
      blobType: blob.type
    });

    return { blob, filename };
  } catch (error) {
    console.error('❌ [PDF] Error generating proposal PDF:', error);
    console.error('❌ [PDF] Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      proposalId: proposal?.id,
      hasOffers: !!proposal?.offers
    });
    return null;
  }
}

/**
 * Sends a proposal link via email
 */
export async function sendProposalLinkViaEmail(proposalId: string, businessId: string, customerEmail: string) {
  console.log('🚀 [EMAIL] sendProposalLinkViaEmail called', {
    proposalId,
    businessId,
    customerEmail,
    timestamp: new Date().toISOString()
  });
  
  try {
    if (!supabase) {
      console.error('❌ [EMAIL] Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log('📧 [EMAIL] Starting to send proposal link via email:', {
      proposalId,
      businessId,
      customerEmail
    });

    // Get or create a token for this proposal
    const tokenData = await getOrCreateProposalToken(proposalId, businessId);

    const publicUrl = await buildProposalCustomerLink(tokenData, businessId);
    console.log('\n=== PROPOSAL LINK (EMAIL) ===');
    console.log(publicUrl);
    console.log('===================\n');
    
    // Get proposal info, business name, logo, and customer first name for better email content
    const { data: proposal, error: proposalError } = await supabase
      .from('proposals')
      .select(`
        title, 
        customer_id,
        business_id,
        businesses:business_id (name),
        customer:customers!proposals_customer_id_fkey(first_name)
      `)
      .eq('id', proposalId)
      .single();
    
    if (proposalError) {
      console.error('Error fetching proposal:', proposalError);
      throw proposalError;
    }

    // Fetch business settings separately to ensure we get booking_settings properly
    // Use EXACT same query pattern as public booking page
    let logoUrl: string | undefined = undefined;
    let businessName = proposal.businesses?.name || "your service provider";
    
    console.log('🚀 STARTING BUSINESS DATA FETCH for logo...');
    console.log('🚀 Business ID:', businessId);
    
    try {
      // Query EXACTLY like the public booking page does
      console.log('📡 Querying businesses table for booking_settings...');
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select('id, name, booking_settings')
        .eq('id', businessId)
        .single();
      
      console.log('📡 Query completed. hasData:', !!businessData, 'hasError:', !!businessError);
      if (businessError) {
        console.error('❌ Business query error:', businessError);
      }
      
      if (!businessError && businessData) {
        console.log('✅ Business data fetched successfully');
        businessName = businessData.name || businessName;
        
        console.log('📦 Raw businessData:', businessData);
        console.log('📦 booking_settings exists?', !!businessData.booking_settings);
        console.log('📦 booking_settings type:', typeof businessData.booking_settings);
        
        // Access EXACTLY like booking page: business?.booking_settings?.logo_url
        // The booking page code: {business?.booking_settings?.logo_url && (
        // So we do: businessData.booking_settings?.logo_url
        if (businessData.booking_settings) {
          console.log('📦 booking_settings value:', businessData.booking_settings);
          console.log('📦 booking_settings keys:', Object.keys(businessData.booking_settings));
          
          // Try multiple access methods
          logoUrl = businessData.booking_settings?.logo_url;
          console.log('🖼️ Logo URL (optional chaining):', logoUrl);
          
          if (!logoUrl) {
            logoUrl = (businessData.booking_settings as any)?.logo_url;
            console.log('🖼️ Logo URL (as any):', logoUrl);
          }
          
          if (!logoUrl) {
            logoUrl = (businessData.booking_settings as any)['logo_url'];
            console.log('🖼️ Logo URL (bracket notation):', logoUrl);
          }
        } else {
          console.error('❌ booking_settings is null/undefined!');
        }
        
        // Final validation
        if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim()) {
          console.log('✅ Logo URL found and valid:', logoUrl);
        } else {
          console.error('❌ Logo URL is missing or invalid');
          console.error('❌ logoUrl value:', logoUrl);
          console.error('❌ logoUrl type:', typeof logoUrl);
          if (businessData.booking_settings) {
            console.error('❌ Full booking_settings:', JSON.stringify(businessData.booking_settings, null, 2));
          }
          logoUrl = undefined;
        }
        
        console.log('🖼️ Final Logo URL before validation:', logoUrl);
        
        // Validate logo URL - it should already be a full Supabase Storage public URL
        if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim()) {
          // Supabase Storage URLs are already absolute, so we use them as-is
          if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
            console.log('✅ Logo URL is valid absolute URL:', logoUrl);
          } else {
            console.warn('⚠️ Logo URL appears to be relative, converting to absolute:', logoUrl);
            const siteUrl = getSiteUrl();
            logoUrl = logoUrl.startsWith('/') ? `${siteUrl}${logoUrl}` : `${siteUrl}/${logoUrl}`;
            console.log('✅ Converted logo URL to absolute:', logoUrl);
          }
        } else {
          console.warn('⚠️ Logo URL is empty or invalid. Type:', typeof logoUrl, 'Value:', logoUrl);
          logoUrl = undefined;
        }
        
        console.log('🖼️ Final Logo URL after validation:', logoUrl);
        console.log('🏢 Business name:', businessName);
      } else {
        console.error('❌ Could not fetch business settings. Error:', businessError);
        console.error('❌ Business ID used:', businessId);
      }
    } catch (error) {
      console.error('❌ EXCEPTION fetching business settings:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
      // Continue without logo
    }
    
    console.log('🏁 BUSINESS DATA FETCH COMPLETE. Final logoUrl:', logoUrl);

    const customerFirstName = (proposal.customer as any)?.first_name || '';

    // Log final values before generating email
    console.log('=== Email Template Generation ===');
    console.log('Customer First Name:', customerFirstName);
    console.log('Business Name:', businessName);
    console.log('Logo URL:', logoUrl);
    console.log('Logo URL type:', typeof logoUrl);
    console.log('Logo URL length:', logoUrl?.length);
    console.log('Logo URL truthy?', !!logoUrl);
    console.log('Proposal Title:', proposal.title);
    console.log('Proposal URL:', publicUrl);
    console.log('==================================');

    // Generate HTML email template
    console.log('📧 About to generate email HTML with logoUrl:', logoUrl);
    console.log('📧 logoUrl type:', typeof logoUrl);
    console.log('📧 logoUrl truthy?', !!logoUrl);
    console.log('📧 logoUrl length:', logoUrl?.length);
    
    const htmlEmailBody = generateProposalEmailHTML(
      customerFirstName,
      businessName,
      proposal.title,
      publicUrl,
      logoUrl
    );
    
    // Debug: Check if logo is in the HTML
    const hasLogoInHTML = htmlEmailBody.includes('img') && htmlEmailBody.includes(logoUrl || '');
    console.log('🔍 Logo in HTML?', hasLogoInHTML);
    console.log('🔍 HTML contains <img tag?', htmlEmailBody.includes('<img'));
    console.log('🔍 HTML length:', htmlEmailBody.length);
    
    // Fetch full proposal data with offers, services, and products for PDF generation
    console.log('🔍 [PDF] Starting PDF generation process...');
    console.log('🔍 [PDF] Fetching full proposal data...');
    
    const fullProposal = await getProposal(proposalId);
    console.log('🔍 [PDF] Full proposal fetched:', {
      proposalId: fullProposal.id,
      hasOffers: !!fullProposal.offers,
      offerCount: fullProposal.offers?.length || 0
    });
    
    // Fetch business data with GST/PST numbers
    console.log('🔍 [PDF] Fetching business data...');
    const { data: businessData, error: businessDataError } = await supabase
      .from('businesses')
      .select('id, name, gst_number, pst_number')
      .eq('id', businessId)
      .single();
    
    if (businessDataError) {
      console.error('❌ [PDF] Error fetching business data for PDF:', businessDataError);
    } else {
      console.log('✅ [PDF] Business data fetched:', {
        hasBusiness: !!businessData,
        hasGST: !!businessData?.gst_number,
        hasPST: !!businessData?.pst_number
      });
    }
    
    // Fetch invoice settings
    console.log('🔍 [PDF] Fetching invoice settings...');
    let invoiceSettings = null;
    try {
      invoiceSettings = await InvoiceSettingsService.getInvoiceSettings(businessId);
      console.log('✅ [PDF] Invoice settings fetched:', !!invoiceSettings);
    } catch (error) {
      console.error('❌ [PDF] Error fetching invoice settings for PDF:', error);
    }
    
    // Respect the "Attach PDF in email" proposal setting (default on).
    let attachPdf = true;
    try {
      const { data: attachPdfSetting } = await supabase.rpc('get_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'proposals',
        p_setting_key: 'attach_pdf_in_email',
        p_default_value: true,
      });
      if (attachPdfSetting === false) attachPdf = false;
    } catch (settingErr) {
      console.warn('⚠️ Could not read attach_pdf_in_email setting; defaulting to attach:', settingErr);
    }

    // Generate PDF
    let pdfAttachment: { filename: string; content: string; type: string } | null = null;
    if (!attachPdf) {
      console.log('📄 [PDF] Skipping PDF attachment — disabled in proposal settings');
    } else {
      try {
        console.log('📄 [PDF] Calling generateProposalPDF function...');
        const pdfData = await generateProposalPDF(fullProposal, businessData, invoiceSettings);
        if (pdfData) {
          // Convert blob to base64
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const base64String = reader.result as string;
              // Remove data:application/pdf;base64, prefix
              const base64Content = base64String.split(',')[1];
              resolve(base64Content);
            };
            reader.onerror = reject;
          });
          reader.readAsDataURL(pdfData.blob);
          const base64Content = await base64Promise;

          pdfAttachment = {
            filename: pdfData.filename,
            content: base64Content,
            type: 'application/pdf'
          };
          console.log('✅ PDF generated successfully:', pdfData.filename);
        } else {
          console.warn('⚠️ PDF generation returned null');
        }
      } catch (error) {
        console.error('❌ Error generating PDF:', error);
        // Continue without PDF attachment
      }
    }
    
    // Fetch proposal photos and prepare attachments
    const attachments: Array<{ filename: string; content: string; type: string }> = [];
    
    // Add PDF as first attachment if generated
    if (pdfAttachment) {
      console.log('📎 [PDF] Adding PDF to attachments:', {
        filename: pdfAttachment.filename,
        type: pdfAttachment.type,
        contentLength: pdfAttachment.content.length,
        contentPreview: pdfAttachment.content.substring(0, 50) + '...'
      });
      attachments.push(pdfAttachment);
      console.log('✅ [PDF] PDF added to attachments array. Total attachments:', attachments.length);
    } else {
      console.warn('⚠️ [PDF] No PDF attachment to add - PDF generation may have failed');
    }
    
    // NOTE: Per-service photos now live on the proposal itself (shown on the
    // public proposal page), so they are no longer attached to the email.

    if (logoUrl && logoUrl.trim()) {
      console.log('🔍 Logo URL in HTML?', htmlEmailBody.includes(logoUrl));
      // Extract the img tag to verify
      const imgMatch = htmlEmailBody.match(/<img[^>]*>/i);
      if (imgMatch) {
        console.log('✅ Image tag found:', imgMatch[0].substring(0, 200));
      } else {
        console.error('❌ No image tag found in HTML even though logoUrl exists!');
        console.error('❌ logoUrl value:', logoUrl);
        console.error('❌ This means the template function is not including the logo!');
        // Let's check what the template actually generated
        const logoCommentMatch = htmlEmailBody.match(/<!-- No logo URL provided[^>]*>/);
        if (logoCommentMatch) {
          console.error('❌ Found "No logo URL provided" comment in HTML:', logoCommentMatch[0]);
        }
      }
    } else {
      console.error('❌ logoUrl is undefined/null/empty - cannot include logo in email');
      console.error('❌ logoUrl value:', logoUrl);
      console.error('❌ Check Business Settings to ensure a logo is uploaded');
      console.error('❌ The business might not have a logo set in booking_settings.logo_url');
    }
    
    // Send email using our Gmail integration
    console.log('📧 Sending email with proposal link...', { 
      hasAttachments: attachments.length > 0,
      attachmentCount: attachments.length,
      attachments: attachments.map(a => ({ 
        filename: a.filename, 
        type: a.type, 
        contentLength: a.content?.length || 0,
        isPDF: a.type === 'application/pdf'
      }))
    });
    
    if (attachments.length === 0) {
      console.warn('⚠️ [PDF] No attachments will be sent with email - PDF generation may have failed');
    }
    const emailResult = await sendEmailWithFallback({
      businessId,
      to: customerEmail,
      subject: `Quote: ${proposal.title}`,
      body: htmlEmailBody,
      attachments: attachments.length > 0 ? attachments : undefined
    });

    if (!emailResult.success) {
      console.error('Error sending email:', emailResult.error);
      throw new Error(emailResult.error || 'Failed to send email');
    }

    console.log(`Email sent successfully via ${emailResult.method}`);

    // Update proposal status to 'sent'
    console.log('Updating proposal status to sent...');
    const { error: updateError } = await supabase
      .from('proposals')
      .update({ status: 'sent' })
      .eq('id', proposalId);

    if (updateError) {
      console.error('Error updating proposal status:', updateError);
      throw updateError;
    }

    // Log the proposal sent event
    await logProposalEvent(
      proposalId, 
      businessId, 
      'sent_email', 
      'Proposal sent via email',
      {
        to_email: customerEmail,
        subject: `Quote: ${proposal.title}`,
        url: publicUrl
      }
    );

    // Create proposal sent milestone
    try {
      if (proposal.customer_id) {
        await createProposalSentMilestone(
          businessId,
          proposal.customer_id,
          proposalId,
          proposal.title,
          'email',
          customerEmail
        );
        console.log('✅ Proposal sent milestone created successfully');
      } else {
        console.warn('⚠️ Skipping milestone creation - missing customer_id:', proposal);
      }
    } catch (milestoneError) {
      console.error('❌ Error creating proposal sent milestone:', milestoneError);
      // Don't throw here as the proposal was sent successfully
    }

    // Record activity for CRM opportunities (non-blocking)
    if (proposal.customer_id) {
      recordOpportunityActivity({
        businessId,
        customerId: proposal.customer_id,
        activityType: 'proposal_sent',
        activityData: {
          proposal_id: proposalId,
          proposal_title: proposal.title,
          method: 'email'
        }
      }).catch(err => {
        console.error('[Proposal Activity] Error recording activity:', err);
      });
    }

    console.log('Proposal status updated successfully');
    return { success: true };
  } catch (error) {
    console.error('Error sending proposal link via email:', error);
    throw error;
  }
}

/**
 * Sends a proposal link via both email and SMS
 */
export async function sendProposalLinkViaBoth(proposalId: string, businessId: string, customerEmail: string, customerPhone: string) {
  console.log('🚀 [BOTH] sendProposalLinkViaBoth called', {
    proposalId,
    businessId,
    customerEmail,
    customerPhone,
    timestamp: new Date().toISOString()
  });
  
  try {
    if (!supabase) {
      console.error('❌ [BOTH] Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log('📧 [BOTH] Starting to send proposal link via both methods:', {
      proposalId,
      businessId,
      customerEmail,
      customerPhone
    });

    // Get or create a token for this proposal
    const tokenData = await getOrCreateProposalToken(proposalId, businessId);

    const publicUrl = await buildProposalCustomerLink(tokenData, businessId);
    console.log('\n=== PROPOSAL LINK (BOTH) ===');
    console.log(publicUrl);
    console.log('===================\n');

    // Track success of each method
    let emailSuccess = false;
    let smsSuccess = false;
    let errorMessage = '';

    // Get proposal info, business name, logo, and customer first name for email/SMS content
    let proposalTitle = "New Proposal";
    let businessName = "your service provider";
    let customerFirstName = '';
    let logoUrl: string | undefined = undefined;
    try {
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .select(`
          title, 
          businesses:business_id (name),
          customer:customers(first_name)
        `)
        .eq('id', proposalId)
        .single();
      
      if (!proposalError && proposal) {
        proposalTitle = proposal.title;
        if (proposal.businesses?.name) {
          businessName = proposal.businesses.name;
        }
        if ((proposal.customer as any)?.first_name) {
          customerFirstName = (proposal.customer as any).first_name;
        }
      }

      // Fetch business settings separately to ensure we get booking_settings properly
      // Use EXACT same query pattern as public booking page
      console.log('🚀 STARTING BUSINESS DATA FETCH for logo (Both)...');
      console.log('🚀 Business ID:', businessId);
      
      try {
        // Query EXACTLY like the public booking page does
        console.log('📡 Querying businesses table for booking_settings (Both)...');
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, booking_settings')
          .eq('id', businessId)
          .single();
        
        console.log('📡 Query completed (Both). hasData:', !!businessData, 'hasError:', !!businessError);
        if (businessError) {
          console.error('❌ Business query error (Both):', businessError);
        }
        
        if (!businessError && businessData) {
          console.log('✅ Business data fetched successfully (Both)');
          businessName = businessData.name || businessName;
          
          console.log('📦 Raw businessData (Both):', businessData);
          console.log('📦 booking_settings exists? (Both):', !!businessData.booking_settings);
          console.log('📦 booking_settings type (Both):', typeof businessData.booking_settings);
          
          // Access EXACTLY like booking page: business?.booking_settings?.logo_url
          if (businessData.booking_settings) {
            console.log('📦 booking_settings value (Both):', businessData.booking_settings);
            console.log('📦 booking_settings keys (Both):', Object.keys(businessData.booking_settings));
            
            // Try multiple access methods
            logoUrl = businessData.booking_settings?.logo_url;
            console.log('🖼️ Logo URL (optional chaining) (Both):', logoUrl);
            
            if (!logoUrl) {
              logoUrl = (businessData.booking_settings as any)?.logo_url;
              console.log('🖼️ Logo URL (as any) (Both):', logoUrl);
            }
            
            if (!logoUrl) {
              logoUrl = (businessData.booking_settings as any)['logo_url'];
              console.log('🖼️ Logo URL (bracket notation) (Both):', logoUrl);
            }
          } else {
            console.error('❌ booking_settings is null/undefined (Both)!');
          }
          
          // Final validation
          if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim()) {
            console.log('✅ Logo URL found and valid (Both):', logoUrl);
          } else {
            console.error('❌ Logo URL is missing or invalid (Both)');
            console.error('❌ logoUrl value (Both):', logoUrl);
            console.error('❌ logoUrl type (Both):', typeof logoUrl);
            if (businessData.booking_settings) {
              console.error('❌ Full booking_settings (Both):', JSON.stringify(businessData.booking_settings, null, 2));
            }
            logoUrl = undefined;
          }
          
          console.log('🖼️ Final Logo URL before validation (Both):', logoUrl);
          
          // Validate logo URL - it should already be a full Supabase Storage public URL
          if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim()) {
            // Supabase Storage URLs are already absolute, so we use them as-is
            if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
              console.log('✅ Logo URL is valid absolute URL (Both):', logoUrl);
            } else {
              console.warn('⚠️ Logo URL appears to be relative, converting to absolute (Both):', logoUrl);
              const siteUrl = getSiteUrl();
              logoUrl = logoUrl.startsWith('/') ? `${siteUrl}${logoUrl}` : `${siteUrl}/${logoUrl}`;
              console.log('✅ Converted logo URL to absolute (Both):', logoUrl);
            }
          } else {
            console.warn('⚠️ Logo URL is empty or invalid (Both). Type:', typeof logoUrl, 'Value:', logoUrl);
            logoUrl = undefined;
          }
          
          console.log('🖼️ Final Logo URL after validation (Both):', logoUrl);
          console.log('🏢 Business name (Both):', businessName);
        } else {
          console.error('❌ Could not fetch business settings (Both). Error:', businessError);
          console.error('❌ Business ID used (Both):', businessId);
        }
      } catch (businessError) {
        console.error('❌ EXCEPTION fetching business settings (Both):', businessError);
        console.error('❌ Error details (Both):', businessError instanceof Error ? businessError.message : String(businessError));
        // Continue without logo
      }
      
      console.log('🏁 BUSINESS DATA FETCH COMPLETE (Both). Final logoUrl:', logoUrl);
    } catch (error) {
      console.error('Error fetching proposal details:', error);
      // Continue with default title and business name
    }

    // Try to send email
    try {
      // Log final values before generating email
      console.log('=== Email Template Generation (Both) ===');
      console.log('Customer First Name:', customerFirstName);
      console.log('Business Name:', businessName);
      console.log('Logo URL:', logoUrl);
      console.log('Proposal Title:', proposalTitle);
      console.log('Proposal URL:', publicUrl);
      console.log('========================================');

      // Generate HTML email template
      const htmlEmailBody = generateProposalEmailHTML(
        customerFirstName,
        businessName,
        proposalTitle,
        publicUrl,
        logoUrl
      );

      // Fetch full proposal data with offers, services, and products for PDF generation
      console.log('🔍 [PDF-BOTH] Starting PDF generation process...');
      console.log('🔍 [PDF-BOTH] Fetching full proposal data...');
      
      const fullProposal = await getProposal(proposalId);
      console.log('🔍 [PDF-BOTH] Full proposal fetched:', {
        proposalId: fullProposal.id,
        hasOffers: !!fullProposal.offers,
        offerCount: fullProposal.offers?.length || 0
      });
      
      // Fetch business data with GST/PST numbers
      console.log('🔍 [PDF-BOTH] Fetching business data...');
      const { data: businessData, error: businessDataError } = await supabase
        .from('businesses')
        .select('id, name, gst_number, pst_number')
        .eq('id', businessId)
        .single();
      
      if (businessDataError) {
        console.error('❌ [PDF-BOTH] Error fetching business data for PDF:', businessDataError);
      } else {
        console.log('✅ [PDF-BOTH] Business data fetched:', {
          hasBusiness: !!businessData,
          hasGST: !!businessData?.gst_number,
          hasPST: !!businessData?.pst_number
        });
      }
      
      // Fetch invoice settings
      console.log('🔍 [PDF-BOTH] Fetching invoice settings...');
      let invoiceSettings = null;
      try {
        invoiceSettings = await InvoiceSettingsService.getInvoiceSettings(businessId);
        console.log('✅ [PDF-BOTH] Invoice settings fetched:', !!invoiceSettings);
      } catch (error) {
        console.error('❌ [PDF-BOTH] Error fetching invoice settings for PDF:', error);
      }
      
      // Generate PDF
      let pdfAttachment: { filename: string; content: string; type: string } | null = null;
      try {
        console.log('📄 [PDF-BOTH] Calling generateProposalPDF function...');
        const pdfData = await generateProposalPDF(fullProposal, businessData, invoiceSettings);
        if (pdfData) {
          // Convert blob to base64
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const base64String = reader.result as string;
              // Remove data:application/pdf;base64, prefix
              const base64Content = base64String.split(',')[1];
              resolve(base64Content);
            };
            reader.onerror = reject;
          });
          reader.readAsDataURL(pdfData.blob);
          const base64Content = await base64Promise;
          
          pdfAttachment = {
            filename: pdfData.filename,
            content: base64Content,
            type: 'application/pdf'
          };
          console.log('✅ [PDF-BOTH] PDF generated successfully:', pdfData.filename);
          console.log('✅ [PDF-BOTH] PDF attachment created:', {
            filename: pdfAttachment.filename,
            type: pdfAttachment.type,
            contentLength: pdfAttachment.content.length
          });
        } else {
          console.warn('⚠️ [PDF-BOTH] PDF generation returned null');
        }
      } catch (error) {
        console.error('❌ [PDF-BOTH] Error generating PDF:', error);
        console.error('❌ [PDF-BOTH] Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        // Continue without PDF attachment
      }

      // Fetch proposal photos and prepare attachments
      const attachments: Array<{ filename: string; content: string; type: string }> = [];
      
      // Add PDF as first attachment if generated
      if (pdfAttachment) {
        console.log('📎 [PDF-BOTH] Adding PDF to attachments:', {
          filename: pdfAttachment.filename,
          type: pdfAttachment.type,
          contentLength: pdfAttachment.content.length,
          contentPreview: pdfAttachment.content.substring(0, 50) + '...'
        });
        attachments.push(pdfAttachment);
        console.log('✅ [PDF-BOTH] PDF added to attachments array. Total attachments:', attachments.length);
      } else {
        console.warn('⚠️ [PDF-BOTH] No PDF attachment to add - PDF generation may have failed');
      }
      
      // Per-service photos live on the proposal (public page), not the email.

      const emailResult = await sendEmailWithFallback({
        businessId,
        to: customerEmail,
        subject: `Quote: ${proposalTitle}`,
        body: htmlEmailBody,
        attachments: attachments.length > 0 ? attachments : undefined
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send email');
      }

      console.log(`Email sent successfully via ${emailResult.method}`);
      emailSuccess = true;
      
      // Log email sent event
      await logProposalEvent(
        proposalId, 
        businessId, 
        'sent_email', 
        'Proposal sent via email',
        {
          to_email: customerEmail,
          subject: `Quote: ${proposalTitle}`,
          url: publicUrl
        }
      );
    } catch (emailError) {
      console.error('Error sending proposal via email:', emailError);
      errorMessage += `Email sending failed: ${emailError instanceof Error ? emailError.message : 'Unknown error'}.`;
    }

    // Try to send SMS
    try {
      // Send SMS using the existing sendSMS function with iPhone-friendly format
      const smsMessage = `You have received a proposal from ${businessName}.

📋 View your proposal:
${publicUrl}

Tap the full link above to view.`;

      const smsResult = await sendSMS({
        businessId,
        to: customerPhone,
        message: smsMessage
      });

      if (!smsResult.success) {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }

      console.log('SMS sent successfully');
      smsSuccess = true;
      
      // Log SMS sent event
      await logProposalEvent(
        proposalId, 
        businessId, 
        'sent_sms', 
        'Proposal sent via SMS',
        {
          to_phone: customerPhone,
          url: publicUrl
        }
      );
    } catch (smsError) {
      console.error('Error sending proposal via SMS:', smsError);
      errorMessage += ` SMS sending failed: ${smsError instanceof Error ? smsError.message : 'Unknown error'}.`;
    }

    // If neither method succeeded, throw an error
    if (!emailSuccess && !smsSuccess) {
      throw new Error(`Failed to send proposal: ${errorMessage}`);
    }

    // If at least one method succeeded, update the proposal status
    const { error: updateError } = await supabase
      .from('proposals')
      .update({ status: 'sent' })
      .eq('id', proposalId);

    if (updateError) {
      console.error('Error updating proposal status:', updateError);
      // Don't throw here as we successfully sent at least one message
    } else {
      console.log('Proposal status updated successfully');
    }

    // Create proposal sent milestone
    try {
      // Get proposal details for milestone
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .select('title, customer_id')
        .eq('id', proposalId)
        .single();
      
      if (!proposalError && proposal && proposal.customer_id) {
        // Determine recipient string
        let recipient = '';
        if (emailSuccess && smsSuccess) {
          recipient = `${customerEmail} and ${customerPhone}`;
        } else if (emailSuccess) {
          recipient = customerEmail;
        } else if (smsSuccess) {
          recipient = customerPhone;
        }

        await createProposalSentMilestone(
          businessId,
          proposal.customer_id,
          proposalId,
          proposal.title,
          'both',
          recipient
        );
        console.log('✅ Proposal sent milestone created successfully');
        
        // Record activity for CRM opportunities (non-blocking)
        // Determine method: 'both', 'email', or 'sms'
        let method: 'both' | 'email' | 'sms' = 'both';
        if (emailSuccess && !smsSuccess) method = 'email';
        if (!emailSuccess && smsSuccess) method = 'sms';
        
        recordOpportunityActivity({
          businessId,
          customerId: proposal.customer_id,
          activityType: 'proposal_sent',
          activityData: {
            proposal_id: proposalId,
            proposal_title: proposal.title,
            method: method
          }
        }).catch(err => {
          console.error('[Proposal Activity] Error recording activity:', err);
        });
      } else {
        console.warn('⚠️ Skipping milestone creation - missing customer_id or proposal data:', {
          proposalError,
          proposal,
          customer_id: proposal?.customer_id
        });
      }
    } catch (milestoneError) {
      console.error('❌ Error creating proposal sent milestone:', milestoneError);
      // Don't throw here as the proposal was sent successfully
    }

    // Return partial success information
    return { 
      success: true, 
      emailSent: emailSuccess, 
      smsSent: smsSuccess,
      message: errorMessage || undefined 
    };
  } catch (error) {
    console.error('Error sending proposal via both methods:', error);
    throw error;
  }
}

export async function setProposalServiceSelection(serviceId: string, isSelected: boolean) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      throw new Error('Supabase client is not initialized');
    }

    console.log(`Setting service ${serviceId} selection state to ${isSelected}`);

    // Try a direct SQL update first
    const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', {
      query: `UPDATE proposal_services SET is_selected = ${isSelected} WHERE id = '${serviceId}' RETURNING *`,
    });

    if (sqlError) {
      console.error('SQL update error:', sqlError);
      
      // Fall back to regular update
      const { data, error } = await supabase
        .from('proposal_services')
        .update({ is_selected: isSelected })
        .eq('id', serviceId)
        .select();

      if (error) {
        console.error('Error updating service selection:', error);
        throw error;
      }

      console.log('Service selection updated successfully via regular update:', data);
      return { success: true, data };
    }

    console.log('Service selection updated successfully via SQL:', sqlData);
    return { success: true, data: sqlData };
  } catch (error) {
    console.error('Error setting service selection:', error);
    throw error;
  }
}

// Update approve/decline to log events
export async function approveProposalOffer(offerId: string) {
  try {
    // First get the proposal ID and details for the offer
    const { data: offerData, error: offerError } = await supabase
      .from('proposal_offers')
      .select('proposal_id, title')
      .eq('id', offerId)
      .single();
    
    if (offerError) throw offerError;
    
    // Call the RPC to approve the offer
    const { error } = await supabase
      .rpc('approve_proposal_offer', { p_offer_id: offerId });

    if (error) throw error;
    
    // Get the proposal details
    const { data: proposalData, error: proposalError } = await supabase
      .from('proposals')
      .select('business_id, customer_id, title')
      .eq('id', offerData.proposal_id)
      .single();
      
    if (proposalError) throw proposalError;
    
    // Log the event
    await logProposalEvent(
      offerData.proposal_id,
      proposalData.business_id,
      'offer_approved',
      `Offer "${offerData.title}" was approved`,
      { offer_id: offerId }
    );

    // Create proposal approved milestone
    try {
      await createProposalApprovedMilestone(
        proposalData.business_id,
        proposalData.customer_id,
        offerData.proposal_id,
        proposalData.title,
        offerData.title
      );
      console.log('✅ Proposal approved milestone created successfully');
    } catch (milestoneError) {
      console.error('❌ Error creating proposal approved milestone:', milestoneError);
      // Don't throw here as the approval was successful
    }
  } catch (error) {
    console.error('Error approving proposal offer:', error);
    throw error;
  }
}

export async function declineProposalOffer(offerId: string) {
  try {
    // First get the proposal ID for the offer
    const { data: offerData, error: offerError } = await supabase
      .from('proposal_offers')
      .select('proposal_id, title')
      .eq('id', offerId)
      .single();
    
    if (offerError) throw offerError;
    
    // Call the RPC to decline the offer
    const { error } = await supabase
      .rpc('decline_proposal_offer', { p_offer_id: offerId });

    if (error) throw error;
    
    // Get the proposal business ID
    const { data: proposalData, error: proposalError } = await supabase
      .from('proposals')
      .select('business_id')
      .eq('id', offerData.proposal_id)
      .single();
      
    if (proposalError) throw proposalError;
    
    // Log the event
    await logProposalEvent(
      offerData.proposal_id,
      proposalData.business_id,
      'offer_declined',
      `Offer "${offerData.title}" was declined`,
      { offer_id: offerId }
    );
  } catch (error) {
    console.error('Error declining proposal offer:', error);
    throw error;
  }
}

/**
 * Logs a proposal view event for repeat views (when status is already 'viewed' or other)
 * Use this to track subsequent views after the first one
 */
export async function logProposalView(
  proposalId: string,
  businessId: string,
  token: string,
  customerId: string,
  metadata: Record<string, any> = {}
) {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    return null;
  }

  try {
    console.log(`Logging repeat view for proposal ${proposalId}`);
    
    return await logProposalEvent(
      proposalId,
      businessId,
      'viewed',
      'Proposal was viewed again by customer',
      {
        customer_id: customerId,
        token: token,
        user_agent: navigator.userAgent || 'unknown',
        timestamp: new Date().toISOString(),
        is_repeat_view: true,
        ...metadata
      }
    );
  } catch (error) {
    console.error('Error logging proposal view:', error);
    return null;
  }
}

/**
 * Cleans up duplicate proposal tokens, keeping only the most recent one per proposal
 */
export async function cleanupDuplicateTokens(proposalId?: string) {
  try {
    console.log('Cleaning up duplicate proposal tokens...');
    
    let query = supabase
      .from('public_proposal_tokens')
      .select('*')
      .order('proposal_id, created_at', { ascending: false });
    
    if (proposalId) {
      query = query.eq('proposal_id', proposalId);
    }

    const { data: allTokens, error: fetchError } = await query;

    if (fetchError) {
      console.error('Error fetching tokens for cleanup:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!allTokens || allTokens.length === 0) {
      console.log('No tokens found for cleanup');
      return { success: true, deletedCount: 0 };
    }

    // Group tokens by proposal_id
    const tokensByProposal = allTokens.reduce((acc, token) => {
      if (!acc[token.proposal_id]) {
        acc[token.proposal_id] = [];
      }
      acc[token.proposal_id].push(token);
      return acc;
    }, {} as Record<string, any[]>);

    let deletedCount = 0;
    
    // For each proposal, keep only the most recent token and delete the rest
    for (const [proposalId, tokens] of Object.entries(tokensByProposal)) {
      if (tokens.length > 1) {
        // Keep the first one (most recent due to ordering), delete the rest
        const tokensToDelete = tokens.slice(1);
        const deleteIds = tokensToDelete.map(t => t.id);
        
        console.log(`Deleting ${deleteIds.length} duplicate tokens for proposal ${proposalId}`);
        
        const { error: deleteError } = await supabase
          .from('public_proposal_tokens')
          .delete()
          .in('id', deleteIds);
          
        if (deleteError) {
          console.error(`Error deleting duplicate tokens for proposal ${proposalId}:`, deleteError);
        } else {
          deletedCount += deleteIds.length;
          console.log(`Successfully deleted ${deleteIds.length} duplicate tokens for proposal ${proposalId}`);
        }
      }
    }

    console.log(`Cleanup complete. Deleted ${deletedCount} duplicate tokens.`);
    return { success: true, deletedCount };
  } catch (error) {
    console.error('Error in cleanupDuplicateTokens:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Gets or creates a proposal token, with duplicate prevention
 */

